import { DoraLinkButton } from "dora-shared/frontend";
import { useTranslation } from "react-i18next";
import "./ShipmentDialogClientInfoMailLinks.scss";
import { useFormContext } from "react-hook-form";
import { useState } from "react";
import CreateShipmentData, {
  CreateEditShipmentStop,
} from "../CreateEditShipmentData";
import { useSelector, useAppDispatch } from "../../../../redux-store";
import { selectClients } from "../../../../ducks/data/clients/selectors";
import { notifyL } from "../../../../ducks/notifications";
import { useL10n } from "../../../../l10n";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-regular-svg-icons";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import axios from "../../../../axios";
import {
  EMAIL_TEMPLATE_ENDPOINTS,
  EmailTemplateResponse,
  EmailTemplateType,
  fillTemplate,
  htmlToPlainText,
} from "../../../../pages/SettingsPage/TemplatesConfig/emailTemplate";

const ShipmentDialogClientInfoMailLinks = (props: {
  truckNumber: string | null;
  trailerNumber: string | null;
}) => {
  const { truckNumber, trailerNumber } = props;
  const { t } = useTranslation(["shipmentModal", "types"]);
  const l10n = useL10n();
  const dispatch = useAppDispatch();
  const clients = useSelector(selectClients);
  // Which mail link is currently fetching its template (null when idle).
  const [loadingTemplate, setLoadingTemplate] =
    useState<EmailTemplateType | null>(null);

  const { getValues } = useFormContext<CreateShipmentData>();

  // Lines describing a single stop (address, schedule, cargo metrics, pieces).
  const buildStopLines = (
    stop: CreateEditShipmentStop
  ): (string | null | undefined)[] => {
    const countryPostcode = [stop.place?.country, stop.place?.postcode]
      .filter(Boolean)
      .join("-");
    const dateTimeFix = [
      stop.openingDate && l10n.formatDate(stop.openingDate.toISODate()),
      stop.openingTime,
      stop.fix && "(FIX)",
    ]
      .filter(Boolean)
      .join(" ");
    const lmColliWeightCubic = [
      // stop.loadmeters && `${stop.loadmeters} lm`,
      stop.colli && `${stop.colli.toString()} kolli`,
      stop.weight && `${stop.weight.toString()} kg`,
      stop.cubicMeters && `${stop.cubicMeters.toString()} m³`,
    ]
      .filter(Boolean)
      .join(", ");
    const dimensions = [
      stop.length && `l: ${stop.length.toString()} cm`,
      stop.width && `b: ${stop.width.toString()} cm`,
      stop.height && `h: ${stop.height.toString()} cm`,
    ]
      .filter(Boolean)
      .join(" x ");
    return [
      stop.place?.placeName,
      stop.place?.address,
      countryPostcode,
      stop.place?.city,
      dateTimeFix,
      stop.contact && `Kontakt: ${stop.contact}`,
      stop.contactPhone && `Tlf.: ${stop.contactPhone}`,
      stop.ref && `Reference: ${stop.ref}`,
      stop.tag && `CMR mærke: ${stop.tag}`,
      lmColliWeightCubic,
      dimensions,
      ...stop.pieces.map((p) => `${p.quantity} x ${p.unit}`),
    ];
  };

  // Formats a list of stops into a single block of text for a template token.
  const formatStopsForToken = (stops: CreateEditShipmentStop[]): string =>
    stops
      .map((stop) => buildStopLines(stop).filter(Boolean).join("\n"))
      .join("\n\n");

  // Retries an async operation up to `attempts` times before rethrowing.
  const withRetry = async <T,>(
    fn: () => Promise<T>,
    attempts = 3
  ): Promise<T> => {
    let lastError: unknown;
    for (let i = 0; i < attempts; i++) {
      try {
        return await fn();
      } catch (err) {
        lastError = err;
      }
    }
    throw lastError;
  };

  // Fetches the template HTML for the shipment's client: the client-specific
  // one if set, otherwise the org default (also used when no client is set).
  const getTemplateHtml = async (
    type: EmailTemplateType,
    clientId: string | null | undefined
  ): Promise<string> => {
    const endpoint = EMAIL_TEMPLATE_ENDPOINTS[type];
    if (clientId) {
      const res = await axios.get<EmailTemplateResponse | null>(
        `${endpoint}/by-client/${clientId}`
      );
      if (res.data) {
        return res.data.data?.html ?? "";
      }
    }
    const res = await axios.get<EmailTemplateResponse>(endpoint);
    return res.data.data?.html ?? "";
  };

  const buildTemplateBody = async (
    type: EmailTemplateType,
    values: CreateShipmentData
  ): Promise<string> => {
    const html = await withRetry(() => getTemplateHtml(type, values.clientId));
    const client = clients.find((c) => c.id === values.clientId);
    const tokenValues: Record<string, string> = {
      orderReference: values.ref ?? "",
      cargoType: t("types:cargoTypes.type", { context: values.type }),
      pickups: formatStopsForToken(values.pickupList),
      dropoffs: formatStopsForToken(values.dropoffList),
      description: values.description ?? "",
      customer: client?.client ?? "",
      invoiceRef: values.invoiceRef ?? "",
      invoiceRef2: values.description2 ?? "",
      truck: truckNumber ?? "",
      trailer: trailerNumber ?? "",
    };
    return fillTemplate(htmlToPlainText(html), tokenValues);
  };

  // Opens the mail link for the given template type using the templated body.
  const onMailLinkClick = async (
    type: EmailTemplateType,
    buildSubject: (ref: string | null | undefined) => string
  ) => {
    const values = getValues();
    const subject = buildSubject(values.ref);
    setLoadingTemplate(type);
    try {
      const body = await buildTemplateBody(type, values);
      const to = values.clientContactEmail ?? "";
      window.location.href = `mailto:${to}?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(body)}`;
    } catch {
      dispatch(
        notifyL({
          type: "error",
          key: "unexpectedError",
          namespace: "notifications",
        })
      );
    } finally {
      setLoadingTemplate(null);
    }
  };

  const onOrderConfirmationClick = () =>
    onMailLinkClick("order-confirmation", (ref) => `Ordrebekræftelse: (${ref})`);

  const onInstructionsClick = () =>
    onMailLinkClick("instructions", (ref) => `Instruks: (${ref})`);

  return (
    <>
      <DoraLinkButton
        className="shipment-dialog__mail-button"
        onClick={onOrderConfirmationClick}
        disabled={loadingTemplate !== null}
      >
        <FontAwesomeIcon
          icon={loadingTemplate === "order-confirmation" ? faSpinner : faEnvelope}
          spin={loadingTemplate === "order-confirmation"}
        />
        <span>{t("shipmentModal:orderConfirmation")}</span>
      </DoraLinkButton>
      <DoraLinkButton
        className="shipment-dialog__mail-button mt-2"
        onClick={onInstructionsClick}
        disabled={loadingTemplate !== null}
      >
        <FontAwesomeIcon
          icon={loadingTemplate === "instructions" ? faSpinner : faEnvelope}
          spin={loadingTemplate === "instructions"}
        />
        <span>{t("shipmentModal:instructions")}</span>
      </DoraLinkButton>
    </>
  );
};

export default ShipmentDialogClientInfoMailLinks;
